import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import AssessmentDashboard from "./results/AssessmentDashboard";
import EB1AssessmentResults from "./results/EB1AssessmentResults";
import AssessmentReportModal from "./AssessmentReportModal";
import { calculateSuccessLikelihood } from "@/lib/assessment/scoring";

interface AssessmentResultsProps {
  data: any;
}

const AssessmentResults = ({ data }: AssessmentResultsProps) => {
  const { toast } = useToast();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Get the pathway type from the assessment data
  const pathwayId = data?.visaPathway?.id || "global-talent";

  // Pathway-specific mock scores
  const mockScores = {
    "eb1": {
      extraordinaryAbility: 85,
      nationalRecognition: 75,
      publications: 90,
      leadership: 80,
      commercialSuccess: 70
    },
    "dubai-golden": {
      investment: 90,
      experience: 85,
      recognition: 80,
      innovation: 75,
      impact: 85
    },
    "high-potential": {
      education: 95,
      university: 90,
      experience: 80,
      skills: 85,
      potential: 90
    },
    "global-talent": {
      education: 90,
      experience: 85,
      skills: 80,
      achievements: 85,
      recognition: 75
    }
  };

  // Get the appropriate scores based on pathway
  const scores = mockScores[pathwayId] || mockScores["global-talent"];

  // Calculate success likelihood
  const likelihood = calculateSuccessLikelihood(scores, {
    education: 80,
    experience: 75,
    skills: 70,
    achievements: 70,
    recognition: 70
  });

  // Mock pathways data
  const mockPathways = [
    {
      id: pathwayId,
      title: data?.visaPathway?.title || "Global Talent Visa",
      match: 85,
      likelihood,
      scores,
      requirements: {
        met: [
          "Educational Qualifications",
          "Professional Experience",
          "Technical Skills",
          "Industry Recognition"
        ],
        missing: [
          "Additional Leadership Experience",
          "International Publications"
        ]
      }
    }
  ];

  // Mock recommendations based on pathway
  const mockRecommendations = {
    "eb1": [
      "Pursue additional leadership roles in your field",
      "Publish research in international journals",
      "Obtain more industry certifications",
      "Document commercial success metrics",
      "Build international recognition"
    ],
    "dubai-golden": [
      "Consider additional investment opportunities",
      "Document innovation achievements",
      "Build regional business network",
      "Showcase economic impact",
      "Develop local partnerships"
    ],
    "high-potential": [
      "Highlight academic achievements",
      "Build professional network in UK",
      "Develop specialized skills",
      "Document leadership potential",
      "Pursue relevant certifications"
    ],
    "global-talent": [
      "Consider pursuing leadership roles in technical projects",
      "Publish research or technical articles",
      "Obtain additional industry certifications",
      "Build a portfolio of innovative projects",
      "Seek international recognition"
    ]
  };

  // Get pathway-specific recommendations
  const recommendations = mockRecommendations[pathwayId] || mockRecommendations["global-talent"];

  // Render EB-1A specific results
  if (pathwayId === "eb1") {
    return <EB1AssessmentResults scores={scores} />;
  }

  // Render standard assessment dashboard for other pathways
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8 animate-fade-in">
      <AssessmentDashboard
        scores={scores}
        pathwayId={pathwayId}
        pathways={mockPathways}
        recommendations={recommendations}
      />

      <AssessmentReportModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        assessmentData={{
          scores,
          pathwayId,
          pathways: mockPathways,
          recommendations
        }}
      />
    </div>
  );
};

export default AssessmentResults;